import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  FileText, AlertTriangle, DollarSign, CalendarClock,
  ArrowUpRight, Users, ShieldAlert, Clock, CheckCircle2, TrendingUp
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const CHART_COLORS = ['#18181B', '#3B82F6', '#52525B', '#A1A1AA', '#D4D4D8', '#F59E0B', '#10B981', '#EF4444'];
const STATUS_COLOR_MAP = {
  DRAFT: '#A1A1AA', APPROVED: '#10B981', EXECUTED: '#3B82F6',
  UNDER_REVIEW: '#F59E0B', IN_VENDOR_REVIEW: '#6366F1',
  REJECTED: '#EF4444', EXPIRED: '#EF4444', CANCELLED: '#64748B',
};

const fmt$ = (v) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
};
const fmtFull = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

/** Decimal-aligned money cell — splits mantissa from K/M suffix so decimals
 *  line up vertically across rows. */
function Money({ value, className = '' }) {
  const v = Number(value || 0);
  let mantissa, suffix;
  if (v >= 1_000_000) { mantissa = (v / 1_000_000).toFixed(1); suffix = 'M'; }
  else if (v >= 1_000) { mantissa = (v / 1_000).toFixed(1); suffix = 'K'; }
  else { mantissa = v.toFixed(0); suffix = ''; }
  return (
    <span className={`inline-flex items-baseline tabular-nums whitespace-nowrap font-semibold ${className}`}>
      <span className="text-right" style={{ minWidth: '4ch' }}>${mantissa}</span>
      <span className="text-left" style={{ minWidth: '1ch' }}>{suffix}</span>
    </span>
  );
}

export default function ContractDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/contracts/dashboard/summary')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load contract dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const drillTo = (params) => {
    const sp = new URLSearchParams({ tab: 'list', ...params });
    navigate(`/data/contracts?${sp.toString()}`);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin h-6 w-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full" />
    </div>
  );
  if (!data) return <div className="text-center py-16 text-zinc-400">Dashboard unavailable</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="contract-dashboard">
      <KPIStrip kpis={k} drillTo={drillTo} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ExpiryRisks risks={data.expiry_risks} expiredActive={data.expired_active} navigate={navigate} drillTo={drillTo} />
        </div>
        <StatusBreakdown data={data.by_status} drillTo={drillTo} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopSuppliers data={data.top_suppliers} drillTo={drillTo} />
        <ExpiryTimeline data={data.expiry_timeline} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ContractAge data={data.contract_age} />
        <ValueByManager data={data.by_manager} drillTo={drillTo} />
      </div>
    </div>
  );
}

/* ────────────────────── KPI STRIP ────────────────────── */
function KPIStrip({ kpis: k, drillTo }) {
  const cards = [
    { label: 'Total Contracts', value: k.total_count, icon: FileText, testid: 'kpi-total-contracts',
      onClick: () => drillTo({ label: 'All Contracts' }) },
    { label: 'Total Value', value: fmt$(k.total_value), icon: DollarSign, testid: 'kpi-total-value',
      onClick: () => drillTo({ label: 'All Contracts' }) },
    { label: 'Active', value: k.active_count, icon: CheckCircle2, testid: 'kpi-active',
      sub: `${k.inactive_count} inactive`, subColor: 'text-zinc-400',
      onClick: () => drillTo({ active: 'true', label: 'Active Contracts' }) },
    { label: 'Expiring (90d)', value: k.expiring_90d, icon: CalendarClock, warn: k.expiring_90d > 0, testid: 'kpi-expiring-90d',
      sub: `${k.expiring_30d} within 30d`, subColor: k.expiring_30d > 0 ? 'text-red-600' : 'text-zinc-400',
      onClick: () => drillTo({ label: 'Expiring Contracts' }) },
    { label: 'Expired', value: k.expired_count, icon: AlertTriangle, warn: k.expired_count > 0, testid: 'kpi-expired',
      onClick: () => drillTo({ label: 'Expired Contracts' }) },
    { label: 'Avg Value', value: fmt$(k.avg_value), icon: TrendingUp, testid: 'kpi-avg-value',
      onClick: () => drillTo({ label: 'All Contracts' }) },
  ];

  return (
    <div className="flex flex-col md:flex-row flex-wrap lg:flex-nowrap border border-zinc-200 rounded-md overflow-hidden shadow-sm" data-testid="contract-kpi-strip">
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <div key={i} onClick={c.onClick}
            className={`flex-1 min-w-[160px] p-4 cursor-pointer transition-all hover:bg-zinc-50 active:bg-zinc-100 group ${i > 0 ? 'border-t md:border-t-0 md:border-l' : ''} border-zinc-200 bg-white`}
            data-testid={c.testid}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Icon className={`h-3.5 w-3.5 ${c.warn ? 'text-red-500' : 'text-zinc-400'}`} strokeWidth={1.5} />
                <span className="text-[11px] font-semibold tracking-wide uppercase text-zinc-500">{c.label}</span>
              </div>
              <ArrowUpRight className="h-3 w-3 text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className={`text-xl font-semibold tracking-tight ${c.warn ? 'text-red-600' : 'text-zinc-900'}`}>{c.value}</p>
            {c.sub && <p className={`text-[11px] mt-0.5 ${c.subColor || 'text-zinc-400'}`}>{c.sub}</p>}
          </div>
        );
      })}
    </div>
  );
}

/* ────────────────────── EXPIRY RISKS ────────────────────── */
function ExpiryRisks({ risks, expiredActive, navigate, drillTo }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="expiry-risks">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-zinc-900">Contract Risks & Renewals</h3>
      </div>
      <div className="p-5 space-y-4">
        {expiredActive.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Expired But Still Active
              <Badge className="bg-red-100 text-red-700 text-[9px] ml-1">{expiredActive.length}</Badge>
            </p>
            <div className="space-y-1 max-h-36 overflow-y-auto">
              {expiredActive.slice(0, 5).map(c => (
                <div key={c.contract_number}
                  className="grid grid-cols-[7.5rem_1fr_auto_5.5rem] items-center gap-3 text-xs px-3 py-2 rounded bg-red-50 hover:bg-red-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`/data/contracts?tab=list&open=${c.contract_number}`)}
                  data-testid={`expired-active-${c.contract_number}`}>
                  <span className="font-mono text-red-700 font-semibold truncate">{c.contract_number}</span>
                  <span className="text-zinc-600 truncate">{c.supplier_name}</span>
                  <Money value={c.value} />
                  <Badge className="bg-red-200 text-red-800 text-[9px] justify-self-end">{c.days_overdue}d overdue</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-2 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Expiring Within 90 Days
            <Badge className="bg-amber-100 text-amber-700 text-[9px] ml-1">{risks.length}</Badge>
          </p>
          {risks.length === 0 ? <p className="text-xs text-zinc-400 italic">No contracts expiring soon</p> : (
            <div className="space-y-1 max-h-44 overflow-y-auto">
              {risks.map(c => (
                <div key={c.contract_number}
                  className="grid grid-cols-[7.5rem_1fr_auto_5.5rem] items-center gap-3 text-xs px-3 py-2 rounded hover:bg-zinc-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/data/contracts?tab=list&open=${c.contract_number}`)}
                  data-testid={`risk-${c.contract_number}`}>
                  <span className="font-mono text-primary font-semibold truncate">{c.contract_number}</span>
                  <span className="text-zinc-500 truncate">{c.supplier_name}</span>
                  <Money value={c.value} />
                  <Badge className={`text-[9px] justify-self-end ${c.days_left <= 30 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {c.days_left}d left
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── STATUS BREAKDOWN (Donut) ────────────────────── */
function StatusBreakdown({ data, drillTo }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  const chartData = data.map(d => ({ ...d, fill: STATUS_COLOR_MAP[d.status] || '#D4D4D8' }));

  const handleClick = (entry) => {
    if (entry && entry.status) {
      drillTo({ status: entry.status, label: `Status: ${entry.status}` });
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="contract-status-breakdown">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">Workflow Status</h3>
      </div>
      <div className="p-5 flex flex-col items-center">
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie data={chartData} dataKey="count" nameKey="status" cx="50%" cy="50%"
              innerRadius={50} outerRadius={75} strokeWidth={2} stroke="#fff"
              onClick={handleClick} cursor="pointer">
              {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip formatter={(val, name) => [`${val} contracts`, name]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="text-center -mt-4 mb-3">
          <p className="text-2xl font-bold text-zinc-900">{total}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Total</p>
        </div>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
          {chartData.map(d => (
            <div key={d.status}
              className="flex items-center gap-1.5 text-xs cursor-pointer hover:opacity-70 transition-opacity"
              onClick={() => handleClick(d)}
              data-testid={`contract-status-legend-${d.status}`}>
              <div className="h-2.5 w-2.5 rounded-full" style={{ background: d.fill }} />
              <span className="text-zinc-600">{d.status.replace(/_/g, ' ')}</span>
              <span className="font-semibold text-zinc-900">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── TOP SUPPLIERS (Bar) ────────────────────── */
function TopSuppliers({ data, drillTo }) {
  const chartData = data.map(d => ({
    ...d,
    name: d.supplier.length > 16 ? d.supplier.slice(0, 14) + '...' : d.supplier,
    fullName: d.supplier
  }));

  const handleClick = (entry) => {
    if (entry && entry.fullName) {
      drillTo({ search: entry.fullName, label: `Supplier: ${entry.fullName}` });
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="contract-top-suppliers">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">Contract Value by Supplier</h3>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
            <XAxis type="number" tickFormatter={v => fmt$(v)} tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#52525b' }} width={110} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => [fmtFull(v), 'Value']} />
            <Bar dataKey="value" fill="#18181B" radius={[0, 4, 4, 0]} barSize={16}
              onClick={handleClick} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────── EXPIRY TIMELINE (Bar) ────────────────────── */
function ExpiryTimeline({ data }) {
  const chartData = data.map(d => ({
    ...d,
    label: d.month.slice(5),
    value: Math.round(d.value)
  }));

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="expiry-timeline">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">Upcoming Expirations</h3>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wide">By month</span>
      </div>
      <div className="p-5">
        {chartData.length === 0 ? (
          <p className="text-xs text-zinc-400 italic text-center py-8">No upcoming expirations</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip formatter={(v, name) => [name === 'count' ? `${v} contracts` : fmtFull(v), name === 'count' ? 'Contracts' : 'Value']} />
              <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={24} name="count" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

/* ────────────────────── CONTRACT AGE (Donut) ────────────────────── */
function ContractAge({ data }) {
  const chartData = data.map((d, i) => ({ ...d, name: d.bucket, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  const total = data.reduce((s, d) => s + d.count, 0);

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="contract-age">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">Contract Age Distribution</h3>
      </div>
      <div className="p-5 flex flex-col items-center">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%"
              innerRadius={45} outerRadius={70} strokeWidth={2} stroke="#fff"
              label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''} labelLine={false}>
              {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip formatter={(val, name) => [`${val} contracts`, name]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-1">
          {chartData.map(d => (
            <div key={d.name} className="flex items-center gap-1.5 text-xs">
              <div className="h-2.5 w-2.5 rounded-full" style={{ background: d.fill }} />
              <span className="text-zinc-600">{d.name}</span>
              <span className="font-semibold text-zinc-900">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── VALUE BY MANAGER (Bar) ────────────────────── */
function ValueByManager({ data, drillTo }) {
  const chartData = data.map(d => ({
    ...d,
    name: d.manager.length > 18 ? d.manager.slice(0, 16) + '...' : d.manager,
    fullName: d.manager
  }));

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="value-by-manager">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <Users className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-zinc-900">Value by Contract Manager</h3>
      </div>
      <div className="p-5">
        {chartData.length === 0 ? (
          <p className="text-xs text-zinc-400 italic text-center py-8">No manager data</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
              <XAxis type="number" tickFormatter={v => fmt$(v)} tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#52525b' }} width={120} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => [fmtFull(v), 'Contract Value']} />
              <Bar dataKey="value" fill="#52525B" radius={[0, 4, 4, 0]} barSize={16}
                onClick={(entry) => entry && entry.fullName && drillTo({ search: entry.fullName, label: `Manager: ${entry.fullName}` })}
                cursor="pointer" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
